/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `IlluInternetProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface IlluInternetProps {
    /**
     * A string representing the CSS class to be applied to the IlluInternetIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#000'
     */
    color1?: string;
    /**
     * A number or string representing the height of the IlluInternetIcon element.
     *
     * @default 96
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'IlluInternet'
     */
    testId?: string;
    /**
     * A number or string representing the height of the IlluInternetIcon element.
     *
     * @default 96
     */
    width?: number | string;
}

/**
 * The `IlluInternet` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `IlluInternetProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the IlluInternetIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the IlluInternetIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the IlluInternetIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const IlluInternetComponent = <IlluInternet className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const IlluInternet = ({
    className = '',
    color1 = '#000',
    height = 96,
    ref,
    testId = 'IlluInternet',
    width = 96
}: IlluInternetProps) => (
    <svg
        ref={ref}
        aria-hidden="true"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 96 96"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M47.5 3.75c2.32 0 4.57.76 6.4 2.17l.36.29 32.27 27.47a10.4 10.4 0 0 1 3.72 7.95v41.21c0 2.77-1.1 5.41-3.07 7.37a10.5 10.5 0 0 1-7.4 3.04H56.72a10.5 10.5 0 0 1-7.4-3.04 10.4 10.4 0 0 1-3.07-7.37v-4.46a6.5 6.5 0 1 1 2.5 0v4.46c0 2.1.84 4.1 2.33 5.6a8 8 0 0 0 5.64 2.31h23.06a8 8 0 0 0 5.64-2.32 8 8 0 0 0 2.33-5.59v-41.2a7.9 7.9 0 0 0-2.82-6.05L52.64 8.12l-.27-.22a8 8 0 0 0-10.03.22L10.08 35.6a8 8 0 0 0-2.83 6.04v41.21c0 2.1.84 4.1 2.33 5.6a8 8 0 0 0 5.64 2.31h16.14a1.25 1.25 0 0 1 0 2.5H15.22a10.5 10.5 0 0 1-7.4-3.04 10.4 10.4 0 0 1-3.07-7.37v-41.2a10.4 10.4 0 0 1 3.72-7.96L40.74 6.2a10.5 10.5 0 0 1 6.76-2.46m0 64.25a4 4 0 1 0 0 8 4 4 0 0 0 0-8m.01-14.77c4.18 0 8.24 1.38 11.5 3.9a1.25 1.25 0 0 1-1.52 1.98 16.4 16.4 0 0 0-9.99-3.38 16.4 16.4 0 0 0-9.99 3.34 1.25 1.25 0 0 1-1.52-1.99 19 19 0 0 1 11.52-3.85m-.01-16.48c8.09 0 15.94 2.64 22.26 7.5a1.25 1.25 0 0 1-1.52 1.98 34 34 0 0 0-20.74-6.98 34 34 0 0 0-20.74 6.98 1.25 1.25 0 0 1-1.52-1.98 36.6 36.6 0 0 1 22.26-7.5" fill={color1} />
    </svg>
);

IlluInternet.displayName = 'IlluInternet';